import {
  StyledCancelButton,
  StyledInformationBox,
  StyledTextField,
  StyledUserTablePagination
} from '../AdminPanelStyling'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useGetUserListDetails} from '../api/getUserDetails'
import {useGetClientName} from '../api/getClientName'
import {useParams} from 'react-router-dom'
import ReusableTable from '../../AdminPanel/common/ReusableTable/ReusableTable'
import IconButton from '@mui/material/IconButton'
import {PersonRounded, WarningRounded} from '@mui/icons-material'
import Modal from '../../components/common/Modal/ModalComponent'
import Button from '@mui/material/Button'
import {validateField, isFormValid as checkFormValid} from '../common/Helpers/formValidation'
import {useAddDeliveryAdmin} from '../api/addDeliveryAdmin'
import Tooltip from '@mui/material/Tooltip'
import {useEditUser} from '../api/editUser'
import {useRemoveUser} from '../api/removeUser'
import TablePaginationActions from '../common/TablePaginationActions'
import {
  StyledAdminGroupHeading,
  StyledChip,
  StyledDetailsBox,
  StyledEditIcon,
  StyledErrorIcon,
  StyledInfoHeading,
  StyledRemoveButton,
  StyledRemoveIcon,
  StyledRemoveUserTitle,
  StyledScrollableBox,
  StyledFlexCenterRow,
  StyledFlexRow,
  StyledFormContainer,
  StyledActionButtonsBox,
  StyledEditFeedbackText
} from './ProjectAdminListStyle'
import Grid from '@mui/material/Unstable_Grid2'
import PopupComponent from '../ProjectDetails/PopupComponent/PopupComponent'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessages from '../common/Snackbar/SnackbarMessages.json'

const CLOSE_CONFIRMATION_TARGETS = {
  ADMIN_FORM: 'adminForm',
  DELETE_CONFIRMATION: 'deleteConfirmation'
}

const DUPLICATE_ADMIN_EMAIL_ERROR = 'This user is already a delivery admin for this client.'

const ProjectAdminList = () => {
  const {clientId} = useParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  })
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: ''
  })
  const [addAdminError, setAddAdminError] = useState('')
  const [isEditMode, setIsEditMode] = useState(false)
  const [showAdminDeleteConfirmation, setShowAdminDeleteConfirmation] = useState(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [pendingCloseTarget, setPendingCloseTarget] = useState(null)
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [snackbar, setSnackbar] = useState({show: false, message: '', isError: false})
  const [isAdminListRefreshing, setIsAdminListRefreshing] = useState(false)
  const [hasLoadedAdmins, setHasLoadedAdmins] = useState(false)

  const {clientNameData, loadingClientName} = useGetClientName(clientId)
  const groupId = clientNameData?.clientDetails?.GROUP_ID

  const [getUserListData, {userData, loadingUserList, refetch, error}] = useGetUserListDetails(groupId)
  const [createUser, {loading: addingAdmin}] = useAddDeliveryAdmin()

  const [editUserResponse, {loading: editingAdmin}] = useEditUser()

  const [deleteUser, {loading: deletingAdmin}] = useRemoveUser()

  useEffect(() => {
    if (groupId) {
      getUserListData({variables: {groupId: Number(groupId), page: page + 1, pageSize: rowsPerPage}})
    }
  }, [groupId, getUserListData, page, rowsPerPage])

  useEffect(() => {
    if (loadingClientName || groupId) {
      setHasLoadedAdmins(false)
      return
    }

    setHasLoadedAdmins(true)
  }, [groupId, loadingClientName])

  useEffect(() => {
    if (!groupId) {
      return
    }

    if (!loadingUserList && (userData !== undefined || error)) {
      setHasLoadedAdmins(true)
    }
  }, [error, groupId, loadingUserList, userData])

  const resetForm = useCallback(() => {
    setFormData({firstName: '', lastName: '', email: ''})
    setErrors({firstName: '', lastName: '', email: ''})
    setAddAdminError('')
  }, [])

  // Populates the fields if in edit mode
  useEffect(() => {
    if (isEditMode && selectedAdmin) {
      setFormData({
        firstName: selectedAdmin.FIRST_NAME || '',
        lastName: selectedAdmin.LAST_NAME || '',
        email: selectedAdmin.EMAIL_ID || ''
      })
    } else resetForm()
  }, [isEditMode, selectedAdmin, resetForm])

  const admins = useMemo(() => userData?.userList?.[0]?.USER_LIST || [], [userData])
  const totalCount = useMemo(() => userData?.userList?.[0]?.TOTAL_COUNT || 0, [userData])

  const existingAdminEmails = useMemo(() => {
    return new Set(admins.map((admin) => (admin.EMAIL_ID || '').trim().toLowerCase()).filter(Boolean))
  }, [admins])

  const hasDuplicateAdminEmail = useMemo(() => {
    if (isEditMode) return false
    const normalizedEmail = formData.email.trim().toLowerCase()
    return normalizedEmail.length > 0 && existingAdminEmails.has(normalizedEmail)
  }, [formData.email, existingAdminEmails, isEditMode])

  const emailFieldError = hasDuplicateAdminEmail ? DUPLICATE_ADMIN_EMAIL_ERROR : errors.email

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage)
  }, [])

  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(+e.target.value)
    setPage(0)
  }, [])

  const refreshAdminList = useCallback(async () => {
    if (!refetch) {
      return
    }

    setIsAdminListRefreshing(true)
    try {
      await refetch()
    } finally {
      setIsAdminListRefreshing(false)
    }
  }, [refetch])

  const handleEdit = (admin) => {
    setSelectedAdmin(admin)
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  const handleRemove = (admin) => {
    setSelectedAdmin(admin)
    setShowAdminDeleteConfirmation(true)
  }

  const handleAddAdmin = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    resetForm()
    setIsEditMode(false)
    setSelectedAdmin(null)
  }

  const hasAdminFormChanged = useMemo(() => {
    const initialFormData =
      isEditMode && selectedAdmin
        ? {
            firstName: selectedAdmin.FIRST_NAME || '',
            lastName: selectedAdmin.LAST_NAME || '',
            email: selectedAdmin.EMAIL_ID || ''
          }
        : {
            firstName: '',
            lastName: '',
            email: ''
          }

    return (
      formData.firstName !== initialFormData.firstName ||
      formData.lastName !== initialFormData.lastName ||
      formData.email !== initialFormData.email
    )
  }, [formData, isEditMode, selectedAdmin])

  const requestCloseModal = () => {
    if (!hasAdminFormChanged) {
      handleCloseModal()
      return
    }

    setPendingCloseTarget(CLOSE_CONFIRMATION_TARGETS.ADMIN_FORM)
    setShowCloseConfirmation(true)
  }

  const handleFormChange = (field) => (e) => {
    const value = e.target.value
    setFormData((prev) => ({...prev, [field]: value}))
    const error = validateField(field, value.trim())
    setErrors((prev) => ({...prev, [field]: error}))
  }

  const isValid = () => {
    return checkFormValid(formData, {...errors, email: emailFieldError}, ['firstName', 'lastName', 'email'])
  }

  const handleSaveAdmin = async () => {
    // Validate all fields before saving
    const firstNameError = validateField('firstName', formData.firstName)
    const lastNameError = validateField('lastName', formData.lastName)
    const emailError = validateField('email', formData.email)

    if (hasDuplicateAdminEmail) {
      setErrors((prev) => ({...prev, email: DUPLICATE_ADMIN_EMAIL_ERROR}))
      return
    }

    if (firstNameError || lastNameError || emailError) {
      setErrors({
        firstName: firstNameError,
        lastName: lastNameError,
        email: emailError
      })
      return
    }

    if (!groupId) {
      setAddAdminError('Missing group id')
      return
    }

    setAddAdminError('')
    const wasEditMode = isEditMode

    try {
      const variables = {
        groupId: groupId.toString(),
        emailId: isEditMode ? formData.email.trim() : undefined,
        email: isEditMode ? undefined : formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim()
      }

      if (isEditMode) {
        const result = await editUserResponse({
          variables
        })

        if (!result?.data?.updatedUserDetails?.updateUser) {
          setAddAdminError('Unable to edit admin. Please try again.')
          return
        }
      } else {
        const result = await createUser({
          variables
        })

        if (!result?.data?.createUser?.createUser) {
          setAddAdminError('Unable to add admin. Please try again.')
          return
        }
      }
      handleCloseModal()

      await refreshAdminList()
      resetForm()
      setPage(0)
      setSnackbar({
        show: true,
        message: wasEditMode ? SnackbarMessages.userEditSuccess : SnackbarMessages.addUserSuccess,
        isError: false
      })
    } catch {
      const mode = isEditMode ? 'edit' : 'add'
      setAddAdminError(`Unable to ${mode} admin. Please try again.`)
    }
  }

  const handleCloseShowAdminDeleteConfirmation = () => setShowAdminDeleteConfirmation(false)

  const requestCloseDeleteConfirmation = () => {
    handleCloseShowAdminDeleteConfirmation()
  }

  const handleCloseConfirmationChange = (isOpen) => {
    setShowCloseConfirmation(isOpen)

    if (!isOpen) {
      setPendingCloseTarget(null)
    }
  }

  const handleConfirmClose = () => {
    if (pendingCloseTarget === CLOSE_CONFIRMATION_TARGETS.ADMIN_FORM) {
      handleCloseModal()
    }

    handleCloseConfirmationChange(false)
  }

  const handleConfirmRemoveAdmin = async () => {
    if (!selectedAdmin || !groupId) {
      return
    }

    try {
      const result = await deleteUser({
        variables: {
          groupId: groupId.toString(),
          email: selectedAdmin.EMAIL_ID
        }
      })

      if (!result?.data?.removeUser?.removeUser) {
        console.error('Failed to remove admin')
        return
      }

      handleCloseShowAdminDeleteConfirmation()
      await refreshAdminList()
      setSelectedAdmin(null)
      setPage(0)
      setSnackbar({show: true, message: SnackbarMessages.userDeleteSuccess, isError: false})
    } catch (error) {
      console.error('Error removing admin:', error)
    }
  }

  const columns = useMemo(
    () => [
      {
        field: 'FIRST_NAME',
        headerName: 'Admin Name',
        renderCell: (row) => `${row.FIRST_NAME || ''} ${row.LAST_NAME || ''}`
      },
      {
        field: 'EMAIL_ID',
        headerName: 'Email Address'
      },
      {
        field: 'role',
        headerName: 'Admin Role',
        renderCell: () => <StyledChip color='warning' label='Delivery Team Admin' icon={<PersonRounded />} />
      },
      {
        field: 'status',
        headerName: 'Status',
        renderCell: (row) => (row.ARCHIVED ? 'Archived' : 'Active')
      },
      {
        field: 'actions',
        headerName: 'Actions',
        align: 'right',
        cellSx: {width: '40px'},
        disableSearch: true,
        renderCell: (row) => (
          <StyledFlexCenterRow>
            <IconButton onClick={() => handleEdit(row)} color='primary' size='small'>
              <StyledEditIcon />
            </IconButton>
            <IconButton onClick={() => handleRemove(row)} color='error' size='small'>
              <StyledRemoveIcon />
            </IconButton>
          </StyledFlexCenterRow>
        )
      }
    ],
    []
  )

  let saveAdminButtonLabel = 'Add'
  if (addingAdmin || editingAdmin) {
    saveAdminButtonLabel = isEditMode ? 'Saving...' : 'Adding...'
  } else if (isEditMode) {
    saveAdminButtonLabel = 'Save Changes'
  }

  const isTableLoading = loadingClientName || loadingUserList || isAdminListRefreshing || (groupId && !hasLoadedAdmins)

  return (
    <>
      <StyledScrollableBox display='flex' flexDirection='column' gap={2}>
        <StyledInformationBox>
          <StyledInfoHeading variant='h6' fontSize={14}>
            Who will have access to this client?
          </StyledInfoHeading>
          <ul>
            <li>
              All Delivery Team Admins working with this client. They will have the ability to create new projects and
              adjust permissions.
            </li>
            <li>All Super Admins of this application.</li>
          </ul>
        </StyledInformationBox>
        <StyledAdminGroupHeading variant='h6' fontSize={16}>
          All admins in this client group
        </StyledAdminGroupHeading>
        {error && (
          <Typography color='error' variant='body2'>
            Failed to load admins. Please try again.
          </Typography>
        )}
        <ReusableTable
          data={admins}
          columns={columns}
          getRowKey={(row) => row.ID}
          emptyMessage='No admins added yet.'
          loading={isTableLoading}
          showAddButton={true}
          addButtonText='Add New Admin'
          onAddClick={handleAddAdmin}
          searchable
          searchPlaceholder='Search admins'
          searchKeys={[(row) => `${row.FIRST_NAME || ''} ${row.LAST_NAME || ''}`.trim(), 'EMAIL_ID']}
        />
        <StyledUserTablePagination
          component='div'
          rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage='Rows per page:'
        />
      </StyledScrollableBox>
      <Modal
        open={isModalOpen}
        onClose={requestCloseModal}
        title={isEditMode ? 'Edit Delivery Team Admin' : 'Add Delivery Team Admin'}
        aria={{labelledBy: isEditMode ? 'edit-delivery-admin-title' : 'add-delivery-admin-title'}}
        maxWidth='sm'
        disableBackdropClick
      >
        <StyledFormContainer>
          {isEditMode && selectedAdmin && (
            <StyledEditFeedbackText>
              You are editing {`${selectedAdmin.FIRST_NAME || ''} ${selectedAdmin.LAST_NAME || ''}`.trim()}
            </StyledEditFeedbackText>
          )}
          <StyledFlexRow>
            <StyledTextField
              label='First Name'
              value={formData.firstName}
              onChange={handleFormChange('firstName')}
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
            <StyledTextField
              label='Last Name'
              value={formData.lastName}
              onChange={handleFormChange('lastName')}
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </StyledFlexRow>
          <Tooltip title={isEditMode ? "User's email cannot be edited" : ''} placement='bottom'>
            <StyledTextField
              label='Email Address'
              value={formData.email}
              onChange={handleFormChange('email')}
              fullWidth
              error={!!emailFieldError}
              helperText={emailFieldError}
              disabled={isEditMode}
            />
          </Tooltip>
          {addAdminError && (
            <Typography color='error' variant='body2'>
              {addAdminError}
            </Typography>
          )}
          <StyledActionButtonsBox>
            <StyledCancelButton variant='text' onClick={requestCloseModal}>
              Cancel
            </StyledCancelButton>
            <Button variant='contained' onClick={handleSaveAdmin} disabled={!isValid() || addingAdmin || editingAdmin}>
              {saveAdminButtonLabel}
            </Button>
          </StyledActionButtonsBox>
        </StyledFormContainer>
      </Modal>
      <Modal
        open={showAdminDeleteConfirmation}
        onClose={requestCloseDeleteConfirmation}
        aria={{
          labelledBy: 'delete-admin-confirmation-title'
        }}
        maxWidth='sm'
        disableBackdropClick
        header={
          <Box display='flex' alignItems='center' gap={1}>
            <StyledErrorIcon />
            <StyledRemoveUserTitle variant='h6' textAlign='center'>
              Remove Admin
            </StyledRemoveUserTitle>
          </Box>
        }
      >
        <Box display='flex' flexDirection='column' px={2} gap={2} mt={-2}>
          <Typography variant='body1'>Are you sure you want to delete this Client Admin?</Typography>
          <StyledDetailsBox>
            <Grid container spacing={2} columns={2}>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  Username:
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  {selectedAdmin ? `${selectedAdmin.FIRST_NAME || ''} ${selectedAdmin.LAST_NAME || ''}` : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} columns={2}>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  User Email:
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  {selectedAdmin?.EMAIL_ID || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} columns={2}>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  User Role:
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography variant='body1' color='CaptionText'>
                  Delivery Team Admin
                </Typography>
              </Grid>
            </Grid>
          </StyledDetailsBox>
          <Box display='flex' justifyContent='space-between' mt={5} mb={3}>
            <StyledFlexCenterRow>
              <WarningRounded color='error' />
              <Typography variant='body2' color='CaptionText' fontStyle='italic'>
                This action cannot be undone.
              </Typography>
            </StyledFlexCenterRow>
            <StyledFlexCenterRow>
              <StyledCancelButton onClick={requestCloseDeleteConfirmation}>Cancel</StyledCancelButton>
              <StyledRemoveButton variant='contained' onClick={handleConfirmRemoveAdmin} disabled={deletingAdmin}>
                {deletingAdmin ? 'Removing...' : 'Remove'}
              </StyledRemoveButton>
            </StyledFlexCenterRow>
          </Box>
        </Box>
      </Modal>
      {showCloseConfirmation && (
        <PopupComponent
          open={showCloseConfirmation}
          setOpen={handleCloseConfirmationChange}
          popupStyle='cancel'
          setGoBack={handleConfirmClose}
          isCompleted={true}
        />
      )}
      <SnackbarComponent
        showNotification={snackbar.show}
        setShowNotification={(show) => setSnackbar((prev) => ({...prev, show}))}
        message={snackbar.message}
        error={snackbar.isError}
      />
    </>
  )
}

export default ProjectAdminList
